import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { TsSnackbarService } from 'tsui';

import { environment } from '@env/environment';
import { Account } from 'app/core/models/account.types';
import { WINDOW } from 'app/providers/window.provider';

type CelesteTokenRequestReason = 'unauthorized' | 'sdk-proactive';

interface CelesteInitOptions {
  platform: string;
  idmToken: string;
  containerMode?: 'overlay' | 'panel';
  containerId?: string;
  disableTracking?: boolean;
  userLocale?: string;
}

interface CelesteTokenResponse {
  token: string;
  expires_at: number;
}

const CELESTE_TOKEN_ENDPOINT = 'celeste_token';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CelesteSDK?: {
      isReady: boolean;
      init: (options: CelesteInitOptions) => void;
      updateIdmToken: (token: string) => void;
      open: () => void;
      close: () => void;
      toggle: () => void;
      destroy: () => void;
    };
  }
}

const CELESTE_SCRIPT_ID = 'celeste-sdk-loader';
const CELESTE_IFRAME_SOURCE = 'CelesteSDK_IFRAME';
const CELESTE_TOKEN_REQUEST = 'CELESTE_SDK_TOKEN_REQUEST';
/** Fixed path the SDK is served from on every tenant host. */
const CELESTE_SDK_PATH = '/celeste/api/sdk/celeste-sdk.js';

/**
 * Celeste never mints its own token — when the iframe needs a fresh one it self-posts a
 * `CELESTE_SDK_TOKEN_REQUEST` message on the host window, and this service answers by
 * fetching a token via {@link getToken} and pushing it back through `updateIdmToken`.
 * Requests arrive both reactively (`unauthorized`, on a 401) and proactively
 * (`sdk-proactive`, ~every 10 min / on refocus); both must be answered or the session
 * eventually breaks.
 */
@Injectable({
  providedIn: 'root',
})
export class CelesteSdkService {
  private readonly window = inject<Window>(WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly snackbar = inject(TsSnackbarService);

  /** Origin of the Celeste iframe (derived from the built SDK URL); used to pin incoming messages. */
  private celesteOrigin: string | null = null;

  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);
  private readonly _failed = signal(false);

  /** True while the SDK script is being fetched/parsed. */
  public readonly loading = this._loading.asReadonly();
  /** True when the most recent load attempt failed (e.g. network/parse error). */
  public readonly failed = this._failed.asReadonly();

  /**
   * Loads the Celeste SDK script (idempotent) and initializes it. The script host is built
   * per-account from the Tahoe tenant alias, so the account must be supplied. Safe to call
   * again after config changes — e.g. once the Tahoe tenant fields are saved — to (re)attempt
   * a load without a full page reload. No-ops when the account has no Tahoe tenant configured,
   * when a load is in flight, or when a load already succeeded; a previous failed attempt is
   * retried.
   */
  public loadSdk(account: Account | null | undefined): void {
    if (this._loading() || this._loaded() || this.window.CelesteSDK?.isReady) {
      return;
    }

    const sdkUrl = this.buildSdkUrl(account);

    // No Tahoe tenant on the account → Celeste stays uninitialized.
    if (!sdkUrl) {
      return;
    }

    this.celesteOrigin = CelesteSdkService.resolveOrigin(sdkUrl);

    // Drop any script tag left over from a previous failed attempt so we can retry cleanly.
    this.document.getElementById(CELESTE_SCRIPT_ID)?.remove();

    this._failed.set(false);
    this._loading.set(true);
    const script = Object.assign(this.document.createElement('script'), {
      type: 'text/javascript',
      id: CELESTE_SCRIPT_ID,
      async: true,
      src: sdkUrl,
      onload: () => {
        this._loading.set(false);
        this._loaded.set(true);
        this.initSdk();
      },
      onerror: (error: unknown) => {
        this._loading.set(false);
        this._failed.set(true);
        this.handleScriptLoadError(error);
      },
    });

    this.window.addEventListener('message', this.handleTokenRequest);

    this.document.head.appendChild(script);
  }

  public open(): void {
    this.window.CelesteSDK?.open();
  }

  public close(): void {
    this.window.CelesteSDK?.close();
  }

  public toggle(): void {
    this.window.CelesteSDK?.toggle();
  }

  /**
   * Mints a short-lived RS256 JWT (via `POST /api/v2/celeste_token`) that authenticates
   * the current user to the Celeste iframe. The standard API auth header is attached by
   * the auth interceptor. Returns an empty token on failure (e.g. `422` when the account
   * isn't configured for Celeste) so init/refresh degrade gracefully rather than throwing.
   */
  public getToken(): Observable<string> {
    return this.http.post<CelesteTokenResponse>(CELESTE_TOKEN_ENDPOINT, {}).pipe(
      map((response) => response?.token ?? ''),
      catchError(() => of('')),
    );
  }

  private handleScriptLoadError(error: unknown): void {
    console.error('Celeste SDK script failed to load:', error);
    this.snackbar.open('Unable to load Celeste. Please try again later.', 'error');
  }

  private initSdk(): void {
    this.getToken()
      .pipe(
        take(1),
        tap((idmToken) =>
          this.window.CelesteSDK?.init({
            platform: environment.celeste.platform,
            idmToken,
            containerMode: 'overlay',
          }),
        ),
      )
      .subscribe();
  }

  private refreshToken(): void {
    this.getToken()
      .pipe(
        take(1),
        tap((token) => this.window.CelesteSDK?.updateIdmToken(token)),
      )
      .subscribe();
  }

  /**
   * Builds the tenant-specific SDK URL from the account's Tahoe tenant alias and the
   * per-environment Celeste domain. Returns null (→ SDK stays uninitialized) unless both
   * `tahoe_tenant_id` and `tahoe_tenant_alias` are present and a domain is configured.
   */
  private buildSdkUrl(account: Account | null | undefined): string | null {
    const alias = account?.tahoe_tenant_alias?.trim();
    const tenantId = account?.tahoe_tenant_id?.trim();
    const domain = environment.celeste?.domain;

    if (!alias || !tenantId || !domain) {
      return null;
    }

    return `https://${alias}.${domain}${CELESTE_SDK_PATH}`;
  }

  private static resolveOrigin(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  private readonly handleTokenRequest = (event: MessageEvent): void => {
    // Only trust messages coming from the Celeste iframe's origin. Fails closed when the
    // origin can't be resolved, so forged/cross-origin messages never mint a token.
    if (!this.celesteOrigin || event.origin !== this.celesteOrigin) {
      return;
    }

    const data = event?.data;

    if (!data || data.source !== CELESTE_IFRAME_SOURCE || data.type !== CELESTE_TOKEN_REQUEST) {
      return;
    }

    // Answer both `unauthorized` and `sdk-proactive`. Ignoring proactive requests lets
    // the token silently expire once the SDK's bounded retry ladder gives up.
    const reason: CelesteTokenRequestReason = data.reason;
    if (reason !== 'unauthorized' && reason !== 'sdk-proactive') {
      return;
    }

    this.refreshToken();
  };
}
